import axios from 'axios';
import API from '../config/API';
import {
  voucherToApiCatalog,
  apiCatalogToVoucher,
  apiInstanceToSale,
} from '../component/gift-cards-v2/giftCardMapper';

// giftCardService — Voucher V2 (gift-card overhaul).
//  - Salon-side management: vouchers (catalog), designs, sales, actions.
//  - Customer purchase: purchaseGiftCard (payment link).
// The salon-side calls go through the existing authed voucher endpoints; the
// giftCardMapper reshapes between the FE voucher/sale shapes and the API.
export const giftCardService = {
  // vouchers (catalog)
  listVouchers,
  saveVoucher,
  deleteVoucher,
  // designs
  listDesigns,
  toggleDesign,
  saveCustomDesign,
  getDesignStats,
  // sales
  listSales,
  getSalesDashboard,
  markRedeemed,
  resendEmail,
  notifyExpiring,
  attachClient,
  getDashboard,
  // customer purchase
  purchaseGiftCard,
  getGiftCardOrder,
};

const headers = () => {
  const accessToken = localStorage.getItem('accessToken') || '';
  return {
    'Content-Type': 'application/json',
    authorization: 'Basic YmV1YXBwOmJldWFwcA==',
    accessToken: `${accessToken}`,
  };
};

// BE success envelope is { statusCode, responseData: { status, message, data } }.
const unwrap = (res) => (res && res.data && res.data.responseData && res.data.responseData.data) || {};

// ---- vouchers (catalog) -----------------------------------------------------
async function listVouchers(opts = {}) {
  const body = {};
  if (opts.search) body.search = opts.search;
  if (opts.status) body.status = opts.status;
  const res = await axios.post(API.VOUCHER_LIST, body, { headers: headers() });
  const catalogs = unwrap(res).catalogs || [];
  return catalogs.map(apiCatalogToVoucher);
}

async function saveVoucher(voucher, currencySymbol) {
  const body = voucherToApiCatalog(voucher, currencySymbol);
  // Existing voucher carries a catalogId (set by voucherToApiCatalog); new ones don't.
  const url = body.catalogId ? API.VOUCHER_UPDATE : API.VOUCHER_CREATE;
  const res = await axios.post(url, body, { headers: headers() });
  return apiCatalogToVoucher(unwrap(res));
}

async function deleteVoucher(id) {
  const res = await axios.post(API.VOUCHER_DELETE, { catalogId: id }, { headers: headers() });
  return unwrap(res);
}

// ---- designs ----------------------------------------------------------------
// Returns the persisted design records (availability overrides + customs).
// The container merges these onto its static editorial/abstract base library.
async function listDesigns() {
  const res = await axios.post(API.VOUCHER_DESIGN_LIST, {}, { headers: headers() });
  return unwrap(res).designs || [];
}

async function toggleDesign(designId, available, extra = {}) {
  const body = { designId, available, ...extra };
  const res = await axios.post(API.VOUCHER_DESIGN_TOGGLE, body, { headers: headers() });
  return unwrap(res);
}

async function saveCustomDesign(design) {
  const res = await axios.post(API.VOUCHER_DESIGN_SAVE_CUSTOM, { design }, { headers: headers() });
  return unwrap(res);
}

// Real per-design conversion analytics — { stats: { [designId]: {...} }, totalRevenue }.
async function getDesignStats() {
  const res = await axios.post(API.VOUCHER_DESIGN_STATS, {}, { headers: headers() });
  return unwrap(res);
}

// ---- sales ------------------------------------------------------------------
// Server-paginated sales list — returns { sales, total }. Pass skip/limit to
// page; search/channel/unclaimed/date filters are applied server-side.
async function listSales(opts = {}) {
  const body = {};
  if (opts.search) body.search = opts.search;
  if (opts.status) body.status = opts.status;
  if (opts.startDate) body.startDate = opts.startDate;
  if (opts.endDate) body.endDate = opts.endDate;
  if (opts.channel) body.channel = opts.channel;
  if (opts.unclaimed) body.unclaimed = true;
  if (opts.skip != null) body.skip = opts.skip;
  if (opts.limit != null) body.limit = opts.limit;
  const res = await axios.post(API.VOUCHER_SALES, body, { headers: headers() });
  const data = unwrap(res);
  return {
    sales: (data.instances || []).map(apiInstanceToSale),
    total: typeof data.total === 'number' ? data.total : (data.instances || []).length,
  };
}

// Server-side Sales tab aggregates: KPIs (with previous-window comparison),
// time series, top vouchers, channel split, expiring + unclaimed.
async function getSalesDashboard(opts = {}) {
  const body = {};
  if (opts.startDate) body.startDate = opts.startDate;
  if (opts.endDate) body.endDate = opts.endDate;
  if (opts.channel) body.channel = opts.channel;
  if (opts.granularity) body.granularity = opts.granularity;
  const res = await axios.post(API.VOUCHER_SALES_DASHBOARD, body, { headers: headers() });
  return unwrap(res);
}

// Mark a sale redeemed (full or partial). Maps to the live /redeem endpoint —
// pass amount (cash) or minutes; bookingId optional.
async function markRedeemed({ voucherCode, amount, minutes, bookingId }) {
  const body = { voucherCode };
  if (amount !== undefined) body.amount = amount;
  if (minutes !== undefined) body.minutes = minutes;
  if (bookingId !== undefined) body.bookingId = bookingId;
  const res = await axios.post(API.VOUCHER_REDEEM, body, { headers: headers() });
  return unwrap(res);
}

async function resendEmail(voucherId) {
  const res = await axios.post(API.VOUCHER_RESEND_EMAIL, { voucherId }, { headers: headers() });
  return unwrap(res);
}

// Email holders of vouchers expiring within `withinDays` (default backend 30).
async function notifyExpiring(withinDays) {
  const body = {};
  if (withinDays !== undefined) body.withinDays = withinDays;
  const res = await axios.post(API.VOUCHER_NOTIFY_EXPIRING, body, { headers: headers() });
  return unwrap(res);
}

// Bind a client (buyer or recipient) to an unclaimed sale.
// role: 'buyer' | 'recipient'; pass clientUserId OR raw { name, email }.
async function attachClient({ voucherId, role, clientUserId, name, email, phone }) {
  const body = { voucherId, role };
  if (clientUserId) body.clientUserId = clientUserId;
  if (name !== undefined) body.name = name;
  if (email !== undefined) body.email = email;
  if (phone !== undefined) body.phone = phone;
  const res = await axios.post(API.VOUCHER_ATTACH_CLIENT, body, { headers: headers() });
  return unwrap(res);
}

async function getDashboard() {
  const res = await axios.get(API.VOUCHER_DASHBOARD, { headers: headers() });
  return unwrap(res);
}

// ---- customer purchase ------------------------------------------------------
// purchaseGiftCard — creates a Stripe Checkout link for a gift-card purchase
// and returns it for the browser to redirect to. Authenticated buyer only
// (the endpoint requires a bearer token). The backend validates the salon +
// voucher + amount, creates a voucher-sale booking, and issues the voucher
// instance on the payment webhook.
// Response (unwrapped): { checkoutUrl | paymentUrl | url, transactionId, ... }.
function purchaseGiftCard(order) {
  const params = {
    salonId: order.salonId,
    voucherId: order.voucherId,
    designId: order.designId,
    amount: order.amount,
    recipient: order.recipient,
    message: order.message,
    fromName: order.fromName,
    deliverAt: order.deliverAt,
    deliveryMethods: order.deliveryMethods,
    successPath: order.successPath,
  };
  return axios.post(API.CREATE_GIFT_CARD_PAYMENT_LINK, params, { headers: headers() });
}

// Look up a gift-card order for the confirmation page (authed buyer). Returns
// the issued code + display fields once the payment webhook has processed.
async function getGiftCardOrder(transactionId) {
  const res = await axios.post(API.GIFT_CARD_ORDER_STATUS, { transactionId }, { headers: headers() });
  return unwrap(res);
}
